// src/helpers/form_validation.js
import { user_data_form_field_placeholders, at_least_one_required_form_identifier_fields } from "./static_vars";

/**
 * Changes the element's border color to red if it's required and empty.
 * @param {HTMLElement} element - The element to validate.
 * @param {boolean} markRed - Whether to mark the element red or not.
 */
export function changeElementErrorStatus(element, markRed) {
    if (markRed) {
        // Add error styling.
        element.classList.add('border-red-500');

        // Only add the input event listener if it hasn't been added yet.
        if (!element.dataset.errorListenerAdded) {
            element.addEventListener('input', () => {
                if (element.value.trim()) {
                    element.classList.remove('border-red-500');
                }
            });
            element.dataset.errorListenerAdded = "true";
        }
    } else {
        // Remove only error styling.
        element.classList.remove('border-red-500');
        // The input listener (added above) is intentionally kept attached —
        // it's idempotent (just removes border-red-500 on input) and the
        // dataset.errorListenerAdded guard prevents duplicate listeners.
        // Attempting removeEventListener with a new function reference is a no-op.
    }
}

/**
 * Shows red border and error message for missing required fields.
 *
 * @param {NodeList} form_elements - The form elements to validate.
 * @param {Array} requiredFields - An array of possibly required fields names.
 * @returns {Array} An array of missing required fields names.
 */
export const showRedBorderRequiredFieldsMissingValues = (
    form_elements,
    requiredFields = at_least_one_required_form_identifier_fields,
    data_hub_instance = window?.data_hub
) => {
    let missingRequiredFields = [];
    let hasAtLeastOneRequiredField = false;

    if (!form_elements || form_elements.length === 0) {
        return data_hub_instance?.error_function?.('showRedBorderRequiredFieldsMissingValues: form_elements is empty', null);
    }

    Array.from(form_elements).forEach(element => {

        // Check if is in the minimal required user_data field array and has value
        if (requiredFields.includes(element.id || element.name) && element.value.trim()) {
            hasAtLeastOneRequiredField = true;
        }

        // First run if it's input.required and has no value change it to red
        const isRequiredAndEmpty = element.required && !element.value.trim();
        if (isRequiredAndEmpty) { missingRequiredFields.push(element.id || element.name); }
        changeElementErrorStatus(element, isRequiredAndEmpty);

    });

    if (!hasAtLeastOneRequiredField) {
        // Missing at least 1 required user data identifier
        Array.from(form_elements).forEach(element => {
            const elementNameOrId = element.id || element.name;
            if (requiredFields.includes(elementNameOrId)) {
                // change the actual element to be required so they cant submit again without value
                element.required = true;
                missingRequiredFields.push(elementNameOrId);
                changeElementErrorStatus(element, true);
            }
        });
    }

    // Only return non duplicates missingRequiredFields
    return Array.from(new Set(missingRequiredFields));
};


/**
 * Validates that all required fields in the form have values.
 * This checks both the HTML required attribute and ensures at least one primary identifier is present.
 * 
 * Handles these scenarios:
 * 1. Zero inputs have values - will fail validation
 * 2. No primary identifier fields (email, cell_phone, etc.) have values - will fail validation
 * 3. ANY input marked as required but empty - will fail validation
 *
 * @param {NodeList|Array} form_elements - The form elements to validate (checks element.value directly).
 * @param {Array} primary_identifier_fields - Array of primary identifier field names (email, cell_phone, etc.).
 * @param {Object} data_hub_instance - The data hub instance for error handling.
 * @returns {Object} An object with:
 *   - valid: {boolean} true if all required fields are filled and at least one primary identifier is present.
 *   - missing: {Array} array of missing required field names.
 */
export const validateAllRequiredFields = (
	form_elements,
	primary_identifier_fields = at_least_one_required_form_identifier_fields,
	data_hub_instance = window?.data_hub
) => {
	const missingRequiredFields = [];
	let hasAtLeastOnePrimaryIdentifier = false;

	if (!form_elements || form_elements.length === 0) {
		if (data_hub_instance?.error_function) {
			data_hub_instance?.error_function?.('validateAllRequiredFields: form_elements is empty', null);
		}
		return { valid: false, missing: [] };
	}

	// First, check all required fields (including non-primary identifiers like first_name)
	Array.from(form_elements).forEach(element => {
		const elementNameOrId = element.id || element.name;
		if (!elementNameOrId) return; // Skip elements without name or id
		
		// Check if element has required attribute and is empty
		// element.required will be true if the attribute is present, even if it's required=""
		// For HTML input elements, element.value is always a string (empty string "" for empty inputs)
		if (element.required === true || element.hasAttribute('required')) {
			let isEmpty = false;
			
			// Check the element's value directly from the DOM
			if (['checkbox', 'radio'].includes(element.type)) {
				isEmpty = !element.checked;
			} else {
				// For text inputs, element.value is always a string
				// Empty input: element.value === "" (falsy, so !element.value is true)
				// Whitespace-only: element.value.trim() === "" (also empty)
				const trimmedValue = typeof element.value === 'string' ? element.value.trim() : String(element.value || '').trim();
				isEmpty = !trimmedValue;
			}
			
			if (isEmpty) {
				missingRequiredFields.push(elementNameOrId);
			}
		}

		// Check if this is a primary identifier field with a value
		// We need to check ALL primary identifier fields in the form, not just required ones
		if (primary_identifier_fields.includes(elementNameOrId)) {
			let hasValue = false;
			if (['checkbox', 'radio'].includes(element.type)) {
				hasValue = element.checked;
			} else {
				// Check if the value is non-empty after trimming
				const trimmedValue = typeof element.value === 'string' ? element.value.trim() : String(element.value || '').trim();
				hasValue = trimmedValue.length > 0;
			}
			if (hasValue) {
				hasAtLeastOnePrimaryIdentifier = true;
			}
		}
	});

	// If no primary identifier is present, we need to ensure at least one is required
	// Add primary identifier fields to missing if none have values
	if (!hasAtLeastOnePrimaryIdentifier) {
		// Check if any primary identifier field exists in the form and mark it as missing
		Array.from(form_elements).forEach(element => {
			const elementNameOrId = element.id || element.name;
			if (primary_identifier_fields.includes(elementNameOrId)) {
				if (!missingRequiredFields.includes(elementNameOrId)) {
					missingRequiredFields.push(elementNameOrId);
				}
			}
		});
	}

	const isValid = missingRequiredFields.length === 0 && hasAtLeastOnePrimaryIdentifier;
	return { valid: isValid, missing: Array.from(new Set(missingRequiredFields)) };
};

/**
 * Generates a comma separated string of the missing required fields.
 * @param {Array} array of missing required fields names.
 * @returns {string} A comma separated string of the missing required fields.
 */
export const generateMissingRequiredFieldsString = (missingRequiredFields) => {
    let missingRequiredFieldsString = null;

    if (missingRequiredFields.length > 0) {
        const friendlyNames = missingRequiredFields.map(key => user_data_form_field_placeholders[key] || key);

        if (friendlyNames.length === 1) {
            missingRequiredFieldsString = friendlyNames[0];
        } else if (friendlyNames.length === 2) {
            missingRequiredFieldsString = `${friendlyNames[0]} and ${friendlyNames[1]}`;
        } else {
            missingRequiredFieldsString = friendlyNames.slice(0, -1).join(', ') + ', and ' + friendlyNames[friendlyNames.length - 1];
        }
    }

    return missingRequiredFieldsString;
}

/**
 * Verifies that the action array is valid.
 * @param {Array} action_array - Array of action objects to validate.
 * @param {Function} error_function - Error handling function to call on validation failure.
 * @returns {boolean} True if valid, otherwise calls error_function.
 */
export function verify_action_array(action_array, error_function) {
    if (!Array.isArray(action_array)) {
        return error_function?.('Action array is not an array');
    }
    // Each object must contain exactly two keys: verb and data.
    for (const action of action_array) {
        if (typeof action !== 'object') {
            return error_function?.('Action array contains non-object');
        }
        if (Object.keys(action).length !== 2) {
            return error_function?.(`Action object does not have 2 keys: ${Object.keys(action)}`);
        }
        if (!action.verb || !action.data) {
            return error_function?.(`Action object does not have verb and data keys: ${Object.keys(action)}`);
        }
        if (!['i_api', 'data_hub_events', 'iterable_api'].includes(action.verb)) {
            return error_function?.(`Action object verb key is not i_api, data_hub_events, or iterable_api: ${action.verb}`);
        }
    }
    return true;
}